import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Modal } from './Modal'
import { Button } from './Button'
import { Badge, BadgeVariant } from './Badge'
import { Banner } from './Banner'
import { Panel } from './Panel'
import { Tabs } from './Tabs'
import { api } from '../services/api'
import styles from './PublishingAuditModal.module.scss'

interface PublishingAuditModalProps {
    /** Match: tc_pub_content_id. VOD: tc_vod_asset_id — a VOD owns its
     *  publishing flow, so the asset is the key, not its pub row. */
    contentId: string
    source?: 'match' | 'vod'
    isOpen: boolean
    onClose: () => void
    venueTimezone?: string | null
}

interface AuditEntry {
    log_id: string
    action: string
    from_status: string | null
    to_status: string | null
    actor: string
    reason: string | null
    details: any
    created_at: string
}

// Diff computation utilities
type DiffType = 'added' | 'removed' | 'changed' | 'unchanged'

interface DiffEntry {
    path: string
    type: DiffType
    oldValue?: any
    newValue?: any
}

function isPrimitive(value: any): boolean {
    return value === null || typeof value !== 'object'
}

// Paths to hide from diff (noise that changes every publish but isn't meaningful)
const HIDDEN_DIFF_PATHS = new Set([
    'sns_response.MessageId',
    'last_updated',
])

function computeObjectDiff(oldObj: any, newObj: any, path: string[] = []): DiffEntry[] {
    const diffs: DiffEntry[] = []
    
    const oldKeys = oldObj ? Object.keys(oldObj) : []
    const newKeys = newObj ? Object.keys(newObj) : []
    const allKeys = new Set([...oldKeys, ...newKeys])
    
    for (const key of allKeys) {
        const currentPath = [...path, key]
        const pathStr = currentPath.join('.')
        
        // Skip hidden paths
        if (HIDDEN_DIFF_PATHS.has(pathStr)) continue
        
        const oldVal = oldObj?.[key]
        const newVal = newObj?.[key]
        
        if (oldVal === undefined && newVal !== undefined) {
            diffs.push({ path: pathStr, type: 'added', newValue: newVal })
        } else if (oldVal !== undefined && newVal === undefined) {
            diffs.push({ path: pathStr, type: 'removed', oldValue: oldVal })
        } else if (isPrimitive(oldVal) || isPrimitive(newVal)) {
            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                diffs.push({ path: pathStr, type: 'changed', oldValue: oldVal, newValue: newVal })
            }
        } else if (Array.isArray(oldVal) && Array.isArray(newVal)) {
            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                diffs.push({ path: pathStr, type: 'changed', oldValue: oldVal, newValue: newVal })
            }
        } else {
            diffs.push(...computeObjectDiff(oldVal, newVal, currentPath))
        }
    }
    
    return diffs
}

function isISODateString(value: any): boolean {
    if (typeof value !== 'string') return false
    // Match ISO 8601 date strings (with or without timezone)
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)
}

function formatTimeInZone(isoString: string, timeZone: string, label: string): string {
    try {
        const date = new Date(isoString)
        if (isNaN(date.getTime())) return ''
        const formatted = date.toLocaleString('en-US', {
            timeZone,
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        return `${label}: ${formatted}`
    } catch {
        return ''
    }
}

function formatValue(value: any): string {
    if (value === null) return 'null'
    if (value === undefined) return 'undefined'
    if (typeof value === 'string') return `"${value}"`
    if (Array.isArray(value)) {
        if (value.length <= 3) return JSON.stringify(value)
        return `[${value.length} items]`
    }
    if (typeof value === 'object') {
        const keys = Object.keys(value)
        if (keys.length <= 2) return JSON.stringify(value)
        return `{${keys.length} fields}`
    }
    return String(value)
}


// Renders a diff value with timezone info if it's a date
const DiffValue: React.FC<{ value: any; venueTimezone?: string | null }> = ({ value, venueTimezone }) => {
    if (!isISODateString(value)) return <>{formatValue(value)}</>
    
    const localTime = venueTimezone ? formatTimeInZone(value, venueTimezone, 'Local') : null
    const pacificTime = formatTimeInZone(value, 'America/Los_Angeles', 'PT')
    
    return (
        <>
            &quot;{value}&quot;
            {(localTime || pacificTime) && (
                <span className={styles.timeZones}>
                    {localTime && <span>{localTime}</span>}
                    {pacificTime && <span>{pacificTime}</span>}
                </span>
            )}
        </>
    )
}

interface AmagiMessage {
    message_id: string
    event: string
    version: number
    timestamp: string
    idempotency_key: string
    trace_id: string
    channels: string[] | null
    eligible_countries: string[] | null
    payload: any
    reason: string | null
    outbound_status: string
    outbound_error: string | null
    retry_count: number
    sns_message_id: string | null
    sns_topic_arn: string | null
    message_sent_at: string | null
    ack_ref: string | null
    ack_status: string | null
    version_received: number | null
    processed_version: number | null
    ack_errors: any[] | null
    ack_timestamp: string | null
    ack_received_at: string | null
    created_at: string
}

export const PublishingAuditModal: React.FC<PublishingAuditModalProps> = ({
    contentId,
    source = 'match',
    isOpen,
    onClose,
    venueTimezone,
}) => {
    const [activeTab, setActiveTab] = useState<'published' | 'amagi' | 'history'>('published')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [history, setHistory] = useState<AuditEntry[]>([])
    const [amagiMessages, setAmagiMessages] = useState<AmagiMessage[]>([])

    // Filter history to show only actual publish events (including cancellations)
    const publishedVersions = history.filter(entry =>
        ['PUBLISH', 'REPUBLISH', 'AUTO_REPUBLISH', 'CANCEL'].includes(entry.action)
    )

    // Compute diffs between consecutive published versions (oldest to newest, but display newest first)
    const versionDiffs = useMemo(() => {
        // Sort by created_at ascending (oldest first) to compute diffs chronologically
        const sorted = [...publishedVersions].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
        
        return sorted.map((entry, index) => {
            const prevEntry = index > 0 ? sorted[index - 1] : null
            const diffs = prevEntry 
                ? computeObjectDiff(prevEntry.details, entry.details)
                : [] // First version shows nothing (it's the baseline)
            
            return {
                entry,
                diffs,
                isFirst: index === 0,
                prevVersion:
                    prevEntry?.details?.version ?? prevEntry?.details?.media_revision,
            }
        }).reverse() // Reverse to show newest first
    }, [publishedVersions])

    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            if (source === 'vod') {
                // One VOD-owned record carries both halves; the tabs below
                // read the same shapes either way.
                const record = await api.get(`/vod/assets/${contentId}/publishing`)
                setHistory(record.history || [])
                setAmagiMessages(record.messages || [])
            } else {
                const [historyResponse, amagiResponse] = await Promise.all([
                    api.get(`/matches/publishing/${contentId}/history`),
                    api.get(`/matches/publishing/${contentId}/amagi-messages`),
                ])
                setHistory(historyResponse.history || [])
                setAmagiMessages(amagiResponse.messages || [])
            }
        } catch (err: any) {
            console.error('Failed to fetch publishing audit data:', err)
            setError(err.message || 'Failed to fetch publishing audit data')
        } finally {
            setLoading(false)
        }
    }, [contentId, source])

    // Earlier attempts at the same transmission, newest first. Every RESEND
    // counts as an attempt; only some carry the superseded verdict, so the
    // count must not be filtered by it or the attempt number undercounts.
    const resendsFor = useCallback(
        (idempotencyKey: string) =>
            history.filter(
                (entry) =>
                    entry.action === 'RESEND' &&
                    entry.details?.idempotency_key === idempotencyKey
            ),
        [history]
    )

    // Fetch data when contentId changes or when modal is opened
    useEffect(() => {
        if (isOpen && contentId) {
            fetchData()
        }
    }, [contentId, isOpen, fetchData])

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'N/A'
        try {
            const date = new Date(dateString)
            return date.toLocaleString()
        } catch {
            return dateString
        }
    }

    const getActionBadgeVariant = (action: string): BadgeVariant => {
        switch (action) {
            case 'PUBLISH':
            case 'REPUBLISH':
            case 'AUTO_REPUBLISH':
                return 'success'
            case 'PUBLISH_QUEUED':
            case 'SCHEDULE':
                return 'secondary'
            case 'PUBLISH_ERROR':
                return 'error'
            case 'CANCEL':
            case 'UNSCHEDULE':
            case 'RECALL':
                return 'warning'
            default:
                return 'neutral'
        }
    }

    const getStatusBadgeVariant = (status: string): BadgeVariant => {
        switch (status.toLowerCase()) {
            case 'sent':
            case 'accepted':
                return 'success'
            case 'pending':
                return 'secondary'
            case 'failed':
            case 'rejected':
                return 'error'
            default:
                return 'neutral'
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Publishing Audit"
            size="large"
            headerActions={
                <>
                    <Button onClick={fetchData} variant="secondary" disabled={loading}>
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button onClick={onClose} variant="secondary">
                        Close
                    </Button>
                </>
            }
        >
            {error && <Banner variant="error" message={error} />}

            <Tabs
                tabs={[
                    { id: 'published', label: 'Published Versions', count: publishedVersions.length },
                    { id: 'amagi', label: 'Messages to Amagi', count: amagiMessages.length },
                    { id: 'history', label: 'All Actions', count: history.length },
                ]}
                activeTab={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId as 'published' | 'amagi' | 'history')}
            >
                {loading ? (
                    <div className={styles.loading}>
                        <p>Loading audit data...</p>
                    </div>
                ) : (
                    <>
                        {activeTab === 'published' && (
                            <div className={styles.diffList}>
                                {versionDiffs.length === 0 ? (
                                    <p className={styles.emptyMessage}>No published versions yet</p>
                                ) : (
                                    versionDiffs.map(({ entry, diffs, isFirst, prevVersion }) => (
                                        <div key={entry.log_id} className={styles.diffEntry}>
                                            <div className={styles.diffHeader}>
                                                <div className={styles.diffHeaderLeft}>
                                                    <Badge variant={getActionBadgeVariant(entry.action)}>
                                                        {entry.action}
                                                    </Badge>
                                                    {(entry.details?.version ?? entry.details?.media_revision) && (
                                                        <span className={styles.versionLabel}>
                                                            v{entry.details.version}
                                                        </span>
                                                    )}
                                                    <span className={styles.actorLabel}>
                                                        {entry.actor}
                                                    </span>
                                                </div>
                                                <span className={styles.diffTimestamp}>
                                                    {formatDate(entry.created_at)}
                                                </span>
                                            </div>
                                            
                                            {isFirst ? (
                                                <div className={styles.diffBaseline}>
                                                    <div className={styles.baselineLabel}>Initial publish</div>
                                                    <pre>{JSON.stringify(entry.details, null, 2)}</pre>
                                                </div>
                                            ) : diffs.length === 0 ? (
                                                <div className={styles.diffNoChanges}>
                                                    No changes from v{prevVersion}
                                                    <details className={styles.detailsExpander}>
                                                        <summary>View full payload</summary>
                                                        <pre>{JSON.stringify(entry.details, null, 2)}</pre>
                                                    </details>
                                                </div>
                                            ) : (
                                                <div className={styles.diffChanges}>
                                                    {diffs.map((diff, idx) => (
                                                        <div 
                                                            key={idx} 
                                                            className={`${styles.diffLine} ${styles[`diff${diff.type.charAt(0).toUpperCase()}${diff.type.slice(1)}`]}`}
                                                        >
                                                            <span className={styles.diffPath}>{diff.path}</span>
                                                            <span className={styles.diffValue}>
                                                                {diff.type === 'added' && (
                                                                    <span className={styles.diffAdd}>+ <DiffValue value={diff.newValue} venueTimezone={venueTimezone} /></span>
                                                                )}
                                                                {diff.type === 'removed' && (
                                                                    <span className={styles.diffRemove}>− <DiffValue value={diff.oldValue} venueTimezone={venueTimezone} /></span>
                                                                )}
                                                                {diff.type === 'changed' && (
                                                                    <>
                                                                        <span className={styles.diffRemove}><DiffValue value={diff.oldValue} venueTimezone={venueTimezone} /></span>
                                                                        <span className={styles.diffArrow}>→</span>
                                                                        <span className={styles.diffAdd}><DiffValue value={diff.newValue} venueTimezone={venueTimezone} /></span>
                                                                    </>
                                                                )}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    <details className={styles.detailsExpander}>
                                                        <summary>View full payload</summary>
                                                        <pre>{JSON.stringify(entry.details, null, 2)}</pre>
                                                    </details>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {activeTab === 'history' && (
                            <div className={styles.historyList}>
                                {history.length === 0 ? (
                                    <p className={styles.emptyMessage}>No publishing history available</p>
                                ) : (
                                    history.map((entry) => (
                                        <Panel key={entry.log_id} padding="compact">
                                            <div className={styles.entryHeader}>
                                                <Badge variant={getActionBadgeVariant(entry.action)}>
                                                    {entry.action}
                                                </Badge>
                                                <span className={styles.timestamp}>
                                                    {formatDate(entry.created_at)}
                                                </span>
                                            </div>
                                            <div className={styles.entryBody}>
                                                {entry.from_status && entry.to_status && (
                                                    <p>
                                                        <strong>Status:</strong> {entry.from_status} → {entry.to_status}
                                                    </p>
                                                )}
                                                {entry.actor && (
                                                    <p>
                                                        <strong>Actor:</strong> {entry.actor}
                                                    </p>
                                                )}
                                                {entry.reason && (
                                                    <p>
                                                        <strong>Reason:</strong> {entry.reason}
                                                    </p>
                                                )}
                                                {entry.details && Object.keys(entry.details).length > 0 && (
                                                    <details className={styles.detailsExpander}>
                                                        <summary>Details</summary>
                                                        <pre>{JSON.stringify(entry.details, null, 2)}</pre>
                                                    </details>
                                                )}
                                            </div>
                                        </Panel>
                                    ))
                                )}
                            </div>
                        )}

                        {activeTab === 'amagi' && (
                            <div className={styles.amagiList}>
                                {amagiMessages.length === 0 ? (
                                    <p className={styles.emptyMessage}>No Amagi messages available</p>
                                ) : (
                                    amagiMessages.map((message) => (
                                        <Panel key={message.message_id} padding="compact">
                                            <div className={styles.entryHeader}>
                                                <div className={styles.headerLeft}>
                                                    <Badge variant={getActionBadgeVariant(message.event)}>
                                                        {message.event}
                                                    </Badge>
                                                    <Badge variant="neutral">v{message.version}</Badge>
                                                    {resendsFor(message.idempotency_key).length > 0 && (
                                                        <Badge variant="neutral">
                                                            attempt{' '}
                                                            {resendsFor(message.idempotency_key).length + 1}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <span className={styles.timestamp}>
                                                    {formatDate(message.timestamp)}
                                                </span>
                                            </div>
                                            <div className={styles.entryBody}>
                                                <div className={styles.statusRow}>
                                                    <div>
                                                        <strong>Outbound:</strong>{' '}
                                                        <Badge variant={getStatusBadgeVariant(message.outbound_status)}>
                                                            {message.outbound_status}
                                                        </Badge>
                                                        {message.outbound_error && (
                                                            <span className={styles.errorText}>
                                                                {' '}
                                                                - {message.outbound_error}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {message.ack_status && (
                                                        <div>
                                                            <strong>Ack:</strong>{' '}
                                                            <Badge variant={getStatusBadgeVariant(message.ack_status)}>
                                                                {message.ack_status}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                </div>

                                                {message.sns_message_id && (
                                                    <p>
                                                        <strong>SNS Message ID:</strong> {message.sns_message_id}
                                                    </p>
                                                )}
                                                {message.message_sent_at && (
                                                    <p>
                                                        <strong>Sent At:</strong> {formatDate(message.message_sent_at)}
                                                    </p>
                                                )}

                                                {message.ack_ref && (
                                                    <div className={styles.ackSection}>
                                                        <h4>Acknowledgement</h4>
                                                        <p>
                                                            <strong>Ref:</strong> {message.ack_ref}
                                                        </p>
                                                        {message.version_received && (
                                                            <p>
                                                                <strong>Version Received:</strong>{' '}
                                                                {message.version_received}
                                                            </p>
                                                        )}
                                                        {message.processed_version && (
                                                            <p>
                                                                <strong>Processed Version:</strong>{' '}
                                                                {message.processed_version}
                                                            </p>
                                                        )}
                                                        {message.ack_timestamp && (
                                                            <p>
                                                                <strong>Ack Timestamp:</strong>{' '}
                                                                {formatDate(message.ack_timestamp)}
                                                            </p>
                                                        )}
                                                        {message.ack_received_at && (
                                                            <p>
                                                                <strong>Received At:</strong>{' '}
                                                                {formatDate(message.ack_received_at)}
                                                            </p>
                                                        )}
                                                        {message.ack_errors && message.ack_errors.length > 0 && (
                                                            <div className={styles.ackErrors}>
                                                                <strong>Errors:</strong>
                                                                <pre>{JSON.stringify(message.ack_errors, null, 2)}</pre>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* The message row only holds the latest
                                                    attempt; earlier ones survive as RESEND
                                                    audit entries. */}
                                                {resendsFor(message.idempotency_key).map((prior, index, all) => (
                                                    <div key={prior.log_id} className={styles.priorAttempt}>
                                                        {/* Newest first, so the entry at index i
                                                            superseded attempt (count - i). */}
                                                        <Badge variant="neutral">
                                                            attempt {all.length - index}
                                                        </Badge>
                                                        {!prior.details?.superseded_attempt ? (
                                                            <Badge variant="neutral">outcome not recorded</Badge>
                                                        ) : prior.details.superseded_attempt.ack_status ? (
                                                            <Badge
                                                                variant={getStatusBadgeVariant(
                                                                    prior.details.superseded_attempt.ack_status
                                                                )}
                                                            >
                                                                {prior.details.superseded_attempt.ack_status}
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="neutral">no ack</Badge>
                                                        )}
                                                        {prior.details?.superseded_attempt && (
                                                            <span className={styles.timestamp}>
                                                                sent{' '}
                                                                {formatDate(
                                                                    prior.details.superseded_attempt
                                                                        .message_sent_at ?? null
                                                                )}
                                                                {prior.details.superseded_attempt
                                                                    .ack_timestamp && (
                                                                    <>
                                                                        {' · acked '}
                                                                        {formatDate(
                                                                            prior.details.superseded_attempt
                                                                                .ack_timestamp
                                                                        )}
                                                                    </>
                                                                )}
                                                            </span>
                                                        )}
                                                        <span className={styles.resendHint}>
                                                            superseded by a republish on{' '}
                                                            {formatDate(prior.created_at)} by {prior.actor}
                                                        </span>
                                                        {prior.details?.superseded_attempt?.ack_errors && (
                                                            <pre>
                                                                {JSON.stringify(
                                                                    prior.details.superseded_attempt.ack_errors,
                                                                    null,
                                                                    2
                                                                )}
                                                            </pre>
                                                        )}
                                                    </div>
                                                ))}

                                                <details className={styles.detailsExpander}>
                                                    <summary>Full Message</summary>
                                                    <pre>{JSON.stringify(message, null, 2)}</pre>
                                                </details>
                                            </div>
                                        </Panel>
                                    ))
                                )}
                            </div>
                        )}
                    </>
                )}
            </Tabs>
        </Modal>
    )
}



